import React, { useEffect, useState } from "react";
import { CreditCard, Users, Layers, UserPlus, Archive, RotateCcw, Pencil, Trash2, ChevronDown, ChevronUp, LifeBuoy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, Badge, ProgressTrack } from "./kit";
import api, { formatApiErrorDetail } from "../lib/api";
import { CATALYST_TIERS, TIER_RANK, fmtLimit } from "../lib/plans";
import { ContactCustomModal } from "./ContactCustomModal";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));
export const goToPlan = () => document.getElementById("plan")?.scrollIntoView({ behavior: "smooth", block: "start" });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

function Head({ icon, title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: "var(--space-4)" }}>
      <span style={{ color: "var(--catalyst-accent, var(--buktu-green))" }}>{icon}</span>
      <h3 style={{ font: "var(--type-heading)", flex: 1 }}>{title}</h3>
      {right}
    </div>
  );
}

export function UsageMeter({ label, used, limit, testid }) {
  const pct = limit ? Math.min(100, Math.round((100 * used) / limit)) : 0;
  return (
    <div data-testid={testid} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", font: "var(--type-body-sm)", fontSize: 13 }}>
        <span style={{ color: "var(--text-muted)" }}>{label}</span>
        <span style={{ font: "var(--type-data)", color: "var(--text-strong)" }}>{used.toLocaleString()} / {fmtLimit(limit)}</span>
      </div>
      <ProgressTrack value={limit ? pct : 0} tone={pct >= 100 ? "risk" : pct >= 90 ? "watch" : "brand"} height={8} />
    </div>
  );
}

export function LimitNotice({ ls, testid = "limit-notice" }) {
  if (!ls || (!ls.warn && !ls.over)) return null;
  const msg = ls.upgrade_required
    ? "You've been over your active member limit for two months. Upgrade to keep adding new members — everyone already in your program keeps full access."
    : ls.over
      ? "You're over your active member limit. You're in a grace period — nothing changes for anyone. We'll only ask you to upgrade after two consecutive months over."
      : `You're at ${ls.pct}% of your active member limit.`;
  return (
    <div data-testid={testid} style={{ display: "flex", gap: 10, padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: ls.upgrade_required ? "var(--status-risk-soft)" : "var(--status-watch-soft)", font: "var(--type-body-sm)", fontSize: 13, color: ls.upgrade_required ? "#8a2a1a" : "#7a5a00" }}>
      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} /><span>{msg}</span>
    </div>
  );
}

export function PlanPanel({ onChanged }) {
  const [p, setP] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState("");
  const [contact, setContact] = useState(false);
  const load = () => api.get("/coach/plan").then((r) => { setP(r.data); setAnnual(r.data.interval === "year"); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!p) return <Card padding="var(--space-6)" id="plan" style={{ marginBottom: "var(--space-5)" }}><p style={{ color: "var(--text-muted)" }}>Loading plan…</p></Card>;

  const act = async (path, body, key) => {
    setBusy(key);
    try {
      const { data } = await api.post(path, body);
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(data.status === "scheduled" ? `Scheduled — takes effect ${fmtDate(data.effective_at)}` : data.status === "kept" ? "Your current plan stays in place" : "Plan updated");
      await load(); onChanged && onChanged();
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const change = (t) => act("/coach/plan/change", { tier: t.id, interval: annual ? "year" : "month", origin_url: window.location.origin }, t.id);
  const curRank = TIER_RANK[p.tier];
  const pd = p.pending_downgrade;
  const interval = annual ? "year" : "month";

  return (
    <Card padding="var(--space-6)" id="plan" data-testid="plan-panel" style={{ marginBottom: "var(--space-5)", scrollMarginTop: 24 }}>
      <Head icon={<CreditCard size={18} />} title="Plan & billing" right={<Badge tone="accent" dot data-testid="plan-current-tier">{p.tier_name}</Badge>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "var(--space-4)", marginBottom: "var(--space-4)" }} className="buktu-plan-grid">
        <UsageMeter label="Active members" used={p.usage.active_members} limit={p.limits.members} testid="plan-usage-members" />
        <UsageMeter label="Cohorts" used={p.usage.cohorts} limit={p.limits.cohorts} testid="plan-usage-cohorts" />
        <UsageMeter label="Team seats" used={p.usage.seats} limit={p.limits.seats} testid="plan-usage-seats" />
      </div>
      <div style={{ display: "flex", gap: "var(--space-6)", flexWrap: "wrap", font: "var(--type-body-sm)", fontSize: 13, color: "var(--text-muted)", marginBottom: "var(--space-4)" }}>
        <span data-testid="plan-interval">Billing: <b style={{ color: "var(--text-strong)" }}>{p.interval ? (p.interval === "year" ? "Annual" : "Monthly") : "Free — no billing"}</b></span>
        <span data-testid="plan-renewal">{p.cancel_at_period_end ? "Ends" : "Renews"}: <b style={{ color: "var(--text-strong)" }}>{fmtDate(p.renewal_date)}</b></span>
        {p.priority_support && <Badge tone="good" dot><LifeBuoy size={11} style={{ marginRight: 4 }} />Priority support</Badge>}
      </div>
      <LimitNotice ls={p.limit_state} testid="plan-limit-notice" />
      {pd && (
        <div data-testid="plan-pending-downgrade" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 12, padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--surface-brand-wash)", font: "var(--type-body-sm)", fontSize: 13 }}>
          <span style={{ flex: 1 }}>Moving to <b>{CATALYST_TIERS.find((t) => t.id === pd.tier)?.name}</b> on {fmtDate(pd.effective_at)}. You keep your current plan until then.</span>
          {p.is_owner && <Button size="sm" variant="secondary" data-testid="plan-keep-btn" disabled={busy === "keep"} onClick={() => act("/coach/plan/keep", {}, "keep")}>Keep current plan</Button>}
        </div>
      )}
      {!p.is_owner ? (
        <p style={{ font: "var(--type-body-sm)", color: "var(--text-faint)", marginTop: 12 }}>Only the account owner can change the plan.</p>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "var(--space-5) 0 var(--space-3)" }}>
            <span style={{ font: "var(--type-label)", flex: 1 }}>Change plan</span>
            <div style={{ display: "inline-flex", gap: 4, padding: 4, borderRadius: 999, border: "1px solid var(--border-hairline)" }}>
              {[["month", "Monthly"], ["year", "Annual · 2 months free"]].map(([v, l]) => (
                <button key={v} data-testid={`plan-interval-${v}`} onClick={() => setAnnual(v === "year")} style={{ border: 0, cursor: "pointer", padding: "5px 12px", borderRadius: 999, font: "var(--type-label)", fontSize: 12, background: interval === v ? "var(--buktu-green)" : "transparent", color: interval === v ? "#fff" : "var(--text-muted)" }}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {CATALYST_TIERS.map((t) => {
              const rank = TIER_RANK[t.id];
              const current = t.id === p.tier && (t.monthly === 0 || t.custom || p.interval === interval);
              const label = t.custom ? "Contact us" : current ? "Current plan" : rank > curRank || (rank === curRank && interval === "year") ? "Upgrade" : "Downgrade";
              const price = t.custom ? "Custom" : t.monthly === 0 ? "$0" : `$${(annual ? t.annual : t.monthly).toLocaleString()}${annual ? "/yr" : "/mo"}`;
              return (
                <div key={t.id} data-testid={`plan-row-${t.id}`} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", border: current ? "2px solid var(--buktu-green)" : "1px solid var(--border-hairline)" }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ font: "var(--type-label)", color: "var(--text-strong)" }}>{t.name} <span style={{ font: "var(--type-data)", fontSize: 12, color: "var(--text-muted)", marginLeft: 6 }}>{price}</span></div>
                    <div style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)" }}>{t.custom ? "2,500+ members · custom cohorts & team" : `${fmtLimit(t.members)} active members · ${t.cohorts} cohort${t.cohorts > 1 ? "s" : ""} · ${t.seats} seat${t.seats > 1 ? "s" : ""}`}</div>
                  </div>
                  <Button size="sm" variant={label === "Upgrade" ? "primary" : "secondary"} data-testid={`plan-action-${t.id}`} disabled={current || !!busy || p.tier === "catalyst_custom"} onClick={() => (t.custom ? setContact(true) : change(t))}>
                    {busy === t.id ? "Working…" : label}
                  </Button>
                </div>
              );
            })}
          </div>
          <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: 12 }}>Upgrades take effect immediately. Downgrades take effect at the end of your paid period. Cancelling stops the next renewal — no prorated refunds.</p>
          {p.tier !== "catalyst_free" && p.tier !== "catalyst_custom" && !p.cancel_at_period_end && (
            <Button size="sm" variant="ghost" data-testid="plan-cancel-btn" disabled={!!busy} onClick={() => { if (window.confirm("Cancel your plan? It stays active until the end of the paid period, then moves to Catalyst Free.")) act("/coach/plan/cancel", {}, "cancel"); }} style={{ marginTop: 6 }}>Cancel plan</Button>
          )}
        </>
      )}
      <ContactCustomModal open={contact} onClose={() => setContact(false)} />
      <style>{`@media (max-width: 640px) { .buktu-plan-grid { grid-template-columns: 1fr !important; } }`}</style>
    </Card>
  );
}

export function CohortsPanel({ isOwner, onChanged }) {
  const [d, setD] = useState(null);
  const [roster, setRoster] = useState([]);
  const [name, setName] = useState("");
  const [limitHit, setLimitHit] = useState("");
  const load = () => api.get("/coach/cohorts").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/coach/overview").then((r) => setRoster(r.data.roster.map((m, i) => ({ id: m.id, name: m.name === "Anonymous member" ? `Anonymous member #${i + 1}` : m.name })))).catch(() => {});
  }, []);
  if (!d) return null;
  const done = () => { load(); onChanged && onChanged(); };
  const create = async () => {
    if (!name.trim()) return;
    try { await api.post("/coach/cohorts", { name }); setName(""); setLimitHit(""); toast.success("Cohort created"); done(); }
    catch (e) { if (e.response?.status === 403) setLimitHit(formatApiErrorDetail(e.response.data.detail)); else err(e); }
  };
  return (
    <Card padding="var(--space-6)" data-testid="cohorts-panel" style={{ marginBottom: "var(--space-5)" }}>
      <Head icon={<Layers size={18} />} title="Cohorts" right={<span data-testid="cohorts-usage" style={{ font: "var(--type-data)", fontSize: 12, color: "var(--text-muted)" }}>{d.active_count} / {fmtLimit(d.limit)} active</span>} />
      {isOwner && (
        <div style={{ display: "flex", gap: 8, marginBottom: "var(--space-3)" }}>
          <input className="buktu-input" data-testid="cohort-new-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New cohort name" style={{ flex: 1 }} onKeyDown={(e) => e.key === "Enter" && create()} />
          <Button size="sm" data-testid="cohort-create-btn" onClick={create}>Create</Button>
        </div>
      )}
      {limitHit && (
        <div data-testid="cohort-limit-msg" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--status-watch-soft)", font: "var(--type-body-sm)", fontSize: 13, color: "#7a5a00", marginBottom: "var(--space-3)" }}>
          <span style={{ flex: 1 }}>{limitHit}</span><Button size="sm" data-testid="cohort-limit-upgrade" onClick={goToPlan}>See upgrade options</Button>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {d.cohorts.map((c) => <CohortRow key={c.id} c={c} roster={roster} isOwner={isOwner} onDone={done} onLimit={setLimitHit} />)}
      </div>
    </Card>
  );
}

function CohortRow({ c, roster, isOwner, onDone, onLimit }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(new Set(c.member_ids));
  const patch = async (body) => {
    try { await api.patch(`/coach/cohorts/${c.id}`, body); setEditing(false); onDone(); }
    catch (e) { if (e.response?.status === 403) onLimit(formatApiErrorDetail(e.response.data.detail)); else err(e); }
  };
  const saveMembers = async () => {
    try { await api.put(`/coach/cohorts/${c.id}/members`, { member_ids: [...sel] }); toast.success("Members updated"); setOpen(false); onDone(); } catch (e) { err(e); }
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div data-testid={`cohort-${c.id}`} style={{ padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)", opacity: c.archived ? 0.6 : 1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {editing ? (
          <input autoFocus className="buktu-input" data-testid={`cohort-rename-input-${c.id}`} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && patch({ name })} style={{ flex: 1 }} />
        ) : (
          <span style={{ flex: 1, font: "var(--type-label)", color: "var(--text-strong)" }}>{c.name} <span style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginLeft: 6 }}>{c.member_ids.length} members</span></span>
        )}
        {c.archived && <Badge tone="neutral">Archived</Badge>}
        {isOwner && (editing
          ? <Button size="sm" data-testid={`cohort-rename-save-${c.id}`} onClick={() => patch({ name })}>Save</Button>
          : <>
              <IconBtn testid={`cohort-rename-${c.id}`} title="Rename" onClick={() => setEditing(true)}><Pencil size={15} /></IconBtn>
              <IconBtn testid={`cohort-archive-${c.id}`} title={c.archived ? "Restore" : "Archive"} onClick={() => patch({ archived: !c.archived })}>{c.archived ? <RotateCcw size={15} /> : <Archive size={15} />}</IconBtn>
              {!c.archived && <IconBtn testid={`cohort-members-${c.id}`} title="Assign members" onClick={() => setOpen((o) => !o)}>{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</IconBtn>}
            </>)}
      </div>
      {open && (
        <div style={{ marginTop: 10, borderTop: "1px solid var(--border-hairline)", paddingTop: 10 }}>
          {roster.length === 0 ? <p style={{ font: "var(--type-body-sm)", color: "var(--text-faint)" }}>No business owners have joined yet.</p> : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 6, maxHeight: 240, overflowY: "auto" }}>
              {roster.map((m) => (
                <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, font: "var(--type-body-sm)", fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" data-testid={`cohort-${c.id}-member-${m.id}`} checked={sel.has(m.id)} onChange={() => toggle(m.id)} />{m.name}
                </label>
              ))}
            </div>
          )}
          <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: 8 }}>A member can belong to more than one cohort and still counts once toward your limit.</p>
          <Button size="sm" data-testid={`cohort-members-save-${c.id}`} onClick={saveMembers} style={{ marginTop: 8 }}>Save members</Button>
        </div>
      )}
    </div>
  );
}

export function TeamPanel({ isOwner }) {
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [limitHit, setLimitHit] = useState("");
  const load = () => api.get("/coach/team").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const invite = async () => {
    if (!email.trim()) return;
    try { await api.post("/coach/team/invite", { email }); setEmail(""); setLimitHit(""); toast.success("Invite sent"); load(); }
    catch (e) { if (e.response?.status === 403) setLimitHit(formatApiErrorDetail(e.response.data.detail)); else err(e); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from your team?`)) return;
    try { await api.delete(`/coach/team/${m.id}`); load(); } catch (e) { err(e); }
  };
  return (
    <Card padding="var(--space-6)" data-testid="team-panel" style={{ marginBottom: "var(--space-5)" }}>
      <Head icon={<Users size={18} />} title="Coaching team" right={<span data-testid="team-usage" style={{ font: "var(--type-data)", fontSize: 12, color: "var(--text-muted)" }}>{d.seats_used} / {fmtLimit(d.seat_limit)} seats</span>} />
      <p style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", marginTop: -8, marginBottom: 12 }}>Team members get dashboard access and can create challenges on plans that include them. Each one confirms the member-privacy acknowledgment before seeing any data. Only you manage billing, branding, settings and the team.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: "var(--space-3)" }}>
        <Row name={d.owner.name || d.owner.email} sub={d.owner.email} badge={<Badge tone="accent">Owner</Badge>} />
        {d.members.map((m) => (
          <Row key={m.id} testid={`team-member-${m.id}`} name={m.name || m.email} sub={m.email}
            badge={<Badge tone={m.status === "active" ? "good" : "neutral"} dot>{m.status === "active" ? "Active" : "Invited"}</Badge>}
            action={isOwner && <IconBtn testid={`team-remove-${m.id}`} title="Remove" onClick={() => remove(m)}><Trash2 size={15} /></IconBtn>} />
        ))}
      </div>
      {isOwner && (
        <div style={{ display: "flex", gap: 8 }}>
          <input className="buktu-input" type="email" data-testid="team-invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@yourprogram.com" style={{ flex: 1 }} />
          <Button size="sm" data-testid="team-invite-btn" iconLeft={<UserPlus size={14} />} onClick={invite}>Invite</Button>
        </div>
      )}
      {limitHit && (
        <div data-testid="team-limit-msg" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "var(--space-3) var(--space-4)", borderRadius: "var(--radius-md)", background: "var(--status-watch-soft)", font: "var(--type-body-sm)", fontSize: 13, color: "#7a5a00", marginTop: "var(--space-3)" }}>
          <span style={{ flex: 1 }}>{limitHit}</span><Button size="sm" data-testid="team-limit-upgrade" onClick={goToPlan}>See upgrade options</Button>
        </div>
      )}
    </Card>
  );
}

function Row({ name, sub, badge, action, testid }) {
  return (
    <div data-testid={testid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "var(--space-2) var(--space-3)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-hairline)" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: "var(--type-label)", color: "var(--text-strong)", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
        <div style={{ font: "var(--type-body-sm)", fontSize: 12, color: "var(--text-faint)", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>
      </div>
      {badge}{action}
    </div>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return <button type="button" data-testid={testid} title={title} onClick={onClick} style={{ border: "1px solid var(--border-hairline)", background: "transparent", borderRadius: 8, width: 30, height: 30, display: "grid", placeItems: "center", cursor: "pointer", color: "var(--text-muted)" }}>{children}</button>;
}
